import { useQuery } from "@apollo/client/react";
import { useEffect } from "react";

import DeltaCounts from "components/DeltaCounts";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import Tab from "ds/components/Tab";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";
import { Stack } from "types/generated";
import { isModuleRunType } from "utils/run";
import { RunContext } from "components/Run/Context";
import { runHasChanges } from "components/Run/helpers";
import useRunObservabilityEnabled from "shared/Run/useRunObservabilityEnabled";

import { GET_RUN_V3 } from "./gql";

const RunHeaderChangesV3Nav = () => {
  const { run, stack } = useTypedContext(RunContext);
  const isObservabilityEnabled = useRunObservabilityEnabled();

  const { data, loading, refetch } = useQuery<{ stack: Stack }>(GET_RUN_V3, {
    variables: { stackId: stack.id, runId: run.id },
  });

  const isModuleRun = isModuleRunType(run.type);
  const isTask = run.type === "TASK";
  const isStackRun = !isTask && !isModuleRun;
  const hasChanges = !!run.delta && runHasChanges(run.delta);
  const hasPluginOutputs = run.pluginLogs && run.pluginLogs.length > 0;

  useEffect(() => {
    if (hasChanges) {
      refetch();
    }
  }, [run.state, hasChanges, refetch]);

  if (loading && !data?.stack?.run) {
    return null;
  }

  // FYI: it validates unstable BE types: BE returns changesV3 as undefined in some cases
  const changesCount = data?.stack?.run?.changesV3?.[0]?.resources.length || 0;

  return (
    <ViewHeaderNavigation>
      <Tab end label="History" to=".." />
      {isStackRun && !!run.delta && !hasChanges && (
        <Typography variant="p-t7" tag="div" margin="0 medium">
          No changes
        </Typography>
      )}

      {isStackRun && hasChanges && changesCount > 0 && (
        <Tab
          label="Changes"
          to="../changes"
          suffixComponent={
            <DeltaCounts delta={run?.delta} skipEmpty variant="flexible" size="small" />
          }
        />
      )}
      {isStackRun && isObservabilityEnabled && <Tab label="Observability" to="../observability" />}
      {hasPluginOutputs && <Tab label="Plugin Outputs" to="../plugin-outputs" />}
    </ViewHeaderNavigation>
  );
};

export default RunHeaderChangesV3Nav;
